// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Sorting conduits into the structures a measurement is taken from:
 *
 * - {@link findParallelGroups} — which sets of conduits near a support could be the rack it carries.
 * - {@link groupPipesIntoTiers} — which of those share a horizontal row.
 * - {@link buildConduitRuns} — which straight lengths make up one continuous run.
 *
 * The first two look across the rack, the last along it: conduits side by side in a row are the same rack
 * but not the same run.
 */

import { TIER_TOLERANCE_FT } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackFrame';
import { MIN_MEASURABLE_LENGTH_FT } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import type {
  ProjectedRackPipe,
  RackPipe,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import type { AutodeskDbId } from 'components/Autodesk/types';
import type { THREE } from 'components/Autodesk/viewerThree';
import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isInsignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/** Minimum absolute dot product between 2 conduit axes for them to count as parallel (≈2.5°). */
const PARALLEL_TOLERANCE = 0.999;
/** The largest cross-section gap between 2 conduits that still puts them on the same rack. */
const MAX_RACK_SPACING_FT = 2;
/** Conduits this close together in the rack cross-section are duplicate geometry for the same conduit. */
const DUPLICATE_CONDUIT_POSITION_TOLERANCE_FT = 0.02;
/** The largest gap between 2 conduit ends that a coupling or bend can still be spanning. */
const MAX_RUN_JOIN_GAP_FT = 2;
/**
 * How closely the offset between 2 conduit ends must run along one of their axes to count as joined.
 * A 90° bend reads ≈0.71 and passes; conduits side by side in a row read ≈0 and are rejected.
 */
const JOINED_DIRECTION_TOLERANCE = 0.5;

/**
 * Group items transitively: any pair {@link arePaired} holds for ends up in the same group. Every group an
 * item pairs with collapses into one, so a late arrival can bridge two that looked separate.
 */
const groupByPredicate = <T>(items: T[], arePaired: (item: T, other: T) => boolean): T[][] =>
  items.reduce<T[][]>((groups, item) => {
    const bridged = groups.filter((group) => group.some((member) => arePaired(item, member)));
    const untouched = groups.filter((group) => !bridged.includes(group));
    return [...untouched, [item, ...bridged.flat()]];
  }, []);

/** True when 2 conduit axes run the same way, pointing in either direction along it. */
const areParallel = (pipe: RackPipe, other: RackPipe): boolean =>
  Math.abs(pipe.axis.dot(other.axis)) >= PARALLEL_TOLERANCE;

/** How far apart 2 parallel conduits sit across the run, with the distance along it taken out. */
const crossSectionOffsetFt = (pipe: RackPipe, other: RackPipe): number => {
  const centerOffset = other.center.clone().sub(pipe.center);
  const alongAxisFt = centerOffset.dot(pipe.axis);
  return centerOffset.sub(pipe.axis.clone().multiplyScalar(alongAxisFt)).length();
};

/** True when 2 parallel conduits cover any of the same length of the run. */
const doStationRangesOverlap = (pipe: RackPipe, other: RackPipe): boolean => {
  // Both are measured along the same axis, so their stations are directly comparable.
  const stationRange = (candidate: RackPipe) => {
    const stations = [candidate.start.dot(pipe.axis), candidate.end.dot(pipe.axis)];
    return { minFt: Math.min(...stations), maxFt: Math.max(...stations) };
  };
  const range = stationRange(pipe);
  const otherRange = stationRange(other);
  return range.minFt <= otherRange.maxFt && otherRange.minFt <= range.maxFt;
};

/** True when 2 conduits are parallel, run alongside each other, and are close enough to share a rack. */
const arePipesOnSameRack = (pipe: RackPipe, other: RackPipe): boolean =>
  areParallel(pipe, other) &&
  crossSectionOffsetFt(pipe, other) <= MAX_RACK_SPACING_FT &&
  doStationRangesOverlap(pipe, other);

/**
 * Every set of mutually parallel, adjacent conduits among `pipes`, largest first — each one a rack the
 * support might be carrying. Size alone cannot pick the winner, so the caller measures them all.
 */
export const findParallelGroups = (pipes: RackPipe[]): RackPipe[][] =>
  groupByPredicate(pipes, arePipesOnSameRack)
    .filter((group) => group.length >= 2)
    .toSorted((a, b) => b.length - a.length);

/** The endpoints of a conduit's centerline. */
const endpointsOf = (pipe: RackPipe): THREE.Vector3[] => [pipe.start, pipe.end];

/**
 * True when 2 conduit ends meet, directly or across whatever short thing joins them — a coupling, a valve,
 * the arc of a bend. The offset must run *along* one of the 2 conduits, so ends that are merely side by
 * side in a row do not count.
 */
const areEndpointsJoined = (endpoint: THREE.Vector3, otherEndpoint: THREE.Vector3, axes: THREE.Vector3[]): boolean => {
  const offset = otherEndpoint.clone().sub(endpoint);
  const gapFt = offset.length();
  if (gapFt > MAX_RUN_JOIN_GAP_FT) return false;
  // Coincident endpoints are joined outright — there is no offset direction left to test.
  if (isInsignificant(gapFt, MIN_MEASURABLE_LENGTH_FT)) return true;

  const direction = offset.normalize();
  return axes.some((axis) => Math.abs(direction.dot(axis)) >= JOINED_DIRECTION_TOLERANCE);
};

/**
 * True when 2 straight lengths lie alongside each other: parallel, offset across the run, and covering some
 * of the same length of it. One conduit cannot be in 2 places at one station, so such a pair is 2 conduits
 * however much else they have in common.
 */
const areConduitsSideBySide = (pipe: RackPipe, other: RackPipe): boolean =>
  areParallel(pipe, other) &&
  crossSectionOffsetFt(pipe, other) > DUPLICATE_CONDUIT_POSITION_TOLERANCE_FT &&
  doStationRangesOverlap(pipe, other);

/**
 * True when 2 straight lengths are pieces of one continuous run: the same element, or 2 elements meeting
 * end to end. Both tests are needed — a bend's 2 legs share an element without touching, and coupled
 * elements touch without sharing an id — and neither is enough on its own to say 2 lengths are one conduit:
 *
 * - One element holds as many conduits as its geometry does, a nested assembly's whole row included, so
 *   sharing an id is evidence of one run only where the 2 lengths could physically be one.
 * - 2 conduits side by side read as joined across their *far* ends, where the offset does run along the
 *   axis. `MAX_RUN_JOIN_GAP_FT` is what rejects that pairing, so it stops rejecting it once the conduits
 *   are shorter than the cap.
 *
 * A pair rejected here still lands in one run when a third length joins to both — correctly, since that
 * third length is the connection between them.
 */
const areConduitsOnSameRun = (pipe: RackPipe, other: RackPipe): boolean =>
  !areConduitsSideBySide(pipe, other) &&
  (pipe.dbId === other.dbId ||
    endpointsOf(pipe).some((endpoint) =>
      endpointsOf(other).some((otherEndpoint) => areEndpointsJoined(endpoint, otherEndpoint, [pipe.axis, other.axis])),
    ));

/** Identifies one continuous conduit run. The conduits alongside it on the rack are separate runs. */
declare const ConduitRunIdSymbol: unique symbol;
export type ConduitRunId = number & { [ConduitRunIdSymbol]: never };

type PipeKey = `${AutodeskDbId}|${string},${string},${string}`;

/** Keyed by element and centerline start: a pipe is copied into a rack frame, so identity does not survive. */
const pipeKeyOf = ({ dbId, start }: RackPipe): PipeKey =>
  `${dbId}|${start.x.toFixed(4)},${start.y.toFixed(4)},${start.z.toFixed(4)}`;

/**
 * Trace which straight lengths belong to one conduit run, so 2 cross-sections can be asked whether they
 * measure the same conduit.
 *
 * @returns a function that returns the run id of a conduit, or `null` for one that was not traced
 */
export const buildConduitRuns = (pipes: RackPipe[]): ((pipe: RackPipe) => ConduitRunId | null) => {
  const runIdByPipe = new Map<string, ConduitRunId>();
  groupByPredicate(pipes, areConduitsOnSameRun).forEach((run, runId) => {
    run.forEach((pipe) => runIdByPipe.set(pipeKeyOf(pipe), runId as ConduitRunId));
  });

  // We're naming the returned callback then returning it because otherwise it gets detected by oxlint as a react component, since it returns null.
  const getConduitRunId = (pipe: RackPipe) => runIdByPipe.get(pipeKeyOf(pipe)) ?? null;
  return getConduitRunId;
};

/** Drop conduits that sit at effectively the same spot on the rack as one already kept. */
const dedupeByPosition = (pipes: ProjectedRackPipe[]): ProjectedRackPipe[] =>
  pipes
    .toSorted((a, b) => a.widthFt - b.widthFt)
    .reduce<ProjectedRackPipe[]>((kept, pipe) => {
      const previous = kept[kept.length - 1];
      const isDuplicate =
        isNotNil(previous) &&
        isAlmostExactlyEqual(previous.widthFt, pipe.widthFt, DUPLICATE_CONDUIT_POSITION_TOLERANCE_FT) &&
        isAlmostExactlyEqual(previous.heightFt, pipe.heightFt, DUPLICATE_CONDUIT_POSITION_TOLERANCE_FT);
      return isDuplicate ? kept : [...kept, pipe];
    }, []);

/** Group a rack's conduits into horizontal rows, bottom to top, each row's conduits left to right. */
export const groupPipesIntoTiers = (pipes: ProjectedRackPipe[]) => {
  const tiers: { referenceHeightFt: number; pipes: ProjectedRackPipe[] }[] = [];

  for (const pipe of pipes.toSorted((a, b) => a.heightFt - b.heightFt)) {
    const tier = tiers.find(({ referenceHeightFt }) =>
      isAlmostExactlyEqual(referenceHeightFt, pipe.heightFt, TIER_TOLERANCE_FT),
    );
    if (isNil(tier)) {
      tiers.push({ referenceHeightFt: pipe.heightFt, pipes: [pipe] });
      continue;
    }
    tier.pipes.push(pipe);
    tier.referenceHeightFt = tier.pipes.reduce((sum, { heightFt }) => sum + heightFt, 0) / tier.pipes.length;
  }

  return tiers
    .toSorted((a, b) => a.referenceHeightFt - b.referenceHeightFt)
    .map(({ referenceHeightFt, pipes: tierPipes }) => ({ referenceHeightFt, pipes: dedupeByPosition(tierPipes) }));
};
